import React, { useState, useEffect, useRef } from 'react'
import { MemoryRouter } from 'react-router-dom'
import { Button, Icon, IconButton, Spinner, PreviewHeader, Checkbox, TooltipLabel } from 'components'
import { useAppDispatch, useAppSelector, useLocalStorage } from 'app/hooks'
import { fetchTemplatePages, cloneTemplateToContext } from 'actions/adminActions'
import { navigate, showPrompt } from 'actions/appActions'
import { PromptType } from 'app/types'
import { logger } from 'helpers/logger'
import { DynamicPage } from 'views/DynamicPage'
import { pageIcons } from 'app/constants'
import { getPageTypeName } from 'helpers/project'
import { getOrganization, getAppById, getTemplateSource } from 'services/appServices'

interface TemplateTileProps {
    template: any
    config: any
    isSelected: boolean
    onSelect: (template: any) => void
}

function TemplateTile({ template, config, isSelected, onSelect }: TemplateTileProps) {
    const [sourceInfo, setSourceInfo] = useState(null)
    const [loadingSource, setLoadingSource] = useState(false)

    useEffect(() => {
        // Fetch source information when template is loaded
        if (template?.id) {
            setLoadingSource(true)
            getTemplateSource(template.id)
                .then((response) => {
                    setSourceInfo(response)
                })
                .catch((error) => {
                    logger.error('Failed to load template source info:', error)
                })
                .finally(() => {
                    setLoadingSource(false)
                })
        }
    }, [template?.id])

    function getSourceUrl() {
        if (!sourceInfo?.source) return null

        if (sourceInfo.source.type === 'app') {
            // For app pages: /{appLink}/{pageLink}
            return `/${sourceInfo.source.link}/${sourceInfo.pageLink}`
        } else if (sourceInfo.source.type === 'organization') {
            // For organization pages: /{organizationLink}/{pageLink}
            return `/${sourceInfo.source.link}/${sourceInfo.pageLink}`
        }

        return null
    }

    function handleViewSource(e: React.MouseEvent) {
        e.stopPropagation() // Prevent tile selection
        const sourceUrl = getSourceUrl()
        if (sourceUrl) {
            window.open(sourceUrl, '_blank')
        }
    }

    return (
        <div
            className={`template-tile${isSelected ? ' selected' : ''} animate__animated animate__fadeInRight animate__fastest`}
            onClick={() => onSelect(template)}
            style={{
                display: 'flex',
                padding: '16px',
                marginBottom: '8px',
                backgroundColor: isSelected ? '#e3f2fd' : '#ffffff',
                border: isSelected ? '2px solid #2196f3' : '1px solid #e0e0e0',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative',
                alignItems: 'flex-start'
            }}
        >
            <div className="template-tile-icon" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '48px',
                height: '48px',
                zoom: 1.5,
                backgroundColor: '#f5f5f5',
                borderRadius: '6px',
                marginRight: '8px',
                flexShrink: 0,
                color: '#666666',
                fontSize: '20px'
            }}>
                <Icon noBg icon={template.icon || pageIcons[template.pageType] || 'fas fa-file'} />
            </div>

            <div className="template-tile-info" style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: '6px'
            }}>
                <div className="row" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    flexWrap: 'wrap'
                }}>
                    <h6 style={{
                        margin: '0',
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#212529',
                        lineHeight: '1.2',
                        width: 'auto'
                    }}>
                        {template.name}
                    </h6>

                    <span className="template-tile-link" style={{
                        fontSize: '14px',
                        color: '#6c757d',
                        fontFamily: 'monospace',
                        backgroundColor: '#f8f9fa',
                        padding: '3px 8px',
                        borderRadius: '4px'
                    }}>
                        /{template.link}
                    </span>
                </div>

                {loadingSource ? (
                    <span style={{
                        fontSize: '14px',
                        color: '#868e96'
                    }}>
                        Loading source...
                    </span>
                ) : sourceInfo?.source ? (
                    <Button
                        onClick={handleViewSource}
                        icon={sourceInfo.source.type === 'app' ? 'fas fa-project-diagram' : 'fas fa-building'}
                        tertiary
                        style={{
                            margin: 0,
                            alignSelf: 'flex-start',
                            fontSize: '12px',
                        }}
                    >
                        {sourceInfo.source.name}
                    </Button>
                ) : (
                    <>
                        {template.appId && (
                            <span className="template-tile-source" style={{
                                fontSize: '12px',
                                color: '#28a745',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}>
                                <Icon noBg icon="fas fa-project-diagram" style={{ fontSize: '10px' }} />
                                Project Template
                            </span>
                        )}

                        {template.organizationId && !template.appId && (
                            <span className="template-tile-source" style={{
                                fontSize: '12px',
                                color: '#17a2b8',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}>
                                <Icon noBg icon="fas fa-building" style={{ fontSize: '10px' }} />
                                Organization Template
                            </span>
                        )}
                    </>
                )}

                {template.templateDescription && (
                    <span className="template-tile-description" style={{
                        fontSize: '13px',
                        color: '#6c757d',
                        lineHeight: '1.3',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    }}>
                        {template.templateDescription}
                    </span>
                )}
            </div>

            {isSelected && (
                <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    color: '#2196f3',
                    fontSize: '16px'
                }}>
                    <Icon noBg icon="fas fa-check-circle" />
                </div>
            )}
        </div>
    )
}

interface TemplateSelectorProps {
    organization: any
    config: any
    onClose: () => void
    onNavigate?: (page: any, options?: { targetApp?: any, targetOrganization?: any, reload?: boolean }) => void
}

export function TemplateSelector(props: TemplateSelectorProps) {
    const { organization, config, onClose, onNavigate } = props
    const dispatch = useAppDispatch()

    const standaloneOrg = useAppSelector((state) => state.app.standaloneOrg)
    const standaloneApp = useAppSelector((state) => state.app.standaloneApp)
    const customDomain = useAppSelector((state) => state.app.customDomain)

    // Get current app context
    const currentProject = useAppSelector((state) => state.app.projectCurrent[0])

    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [expandedTags, setExpandedTags] = useState<Set<string>>(new Set())
    const [loadingTemplates, setLoadingTemplates] = useState(false)
    const [templatePages, setTemplatePages] = useState([])
    const [generatingPage, setGeneratingPage] = useState(false)
    const [templateOrganization, setTemplateOrganization] = useState(null)
    const [templateApp, setTemplateApp] = useState(null)
    const [loadingPreview, setLoadingPreview] = useState(false)
    const [usePlaceholders, setUsePlaceholders] = useLocalStorage('templateUsePlaceholders', true)
    const previewContainerRef = useRef<HTMLDivElement>(null)

    // Get all media from Redux for preview rendering
    const allMedia = useAppSelector((state) => state.app.media)

    useEffect(() => {
        if (!loadingTemplates && templatePages.length === 0) {
            fetchTemplates()
        }
    }, [])

    // Reset scroll position when template changes
    useEffect(() => {
        if (previewContainerRef.current && selectedTemplate) {
            previewContainerRef.current.scrollTop = 0
        }
    }, [selectedTemplate, templateOrganization, templateApp])

    // Fetch full organization or app data when template is selected
    useEffect(() => {
        async function fetchTemplateData() {
            if (!selectedTemplate) {
                setTemplateOrganization(null)
                setTemplateApp(null)
                return
            }

            setLoadingPreview(true)
            try {
                // Check if template belongs to an app or organization
                if (selectedTemplate.appId) {
                    // Fetch app data by ID
                    const appResult = await getAppById(selectedTemplate.appId)

                    if (appResult) {
                        setTemplateApp(appResult)
                        setTemplateOrganization(null)
                    } else {
                        logger.error('TemplateSelector: Failed to load template app data - null result')
                        dispatch(showPrompt({
                            title: 'Preview Error',
                            message: 'Failed to load template preview data.',
                            type: PromptType.Confirm,
                            cancelMessage: null
                        }))
                    }
                } else if (selectedTemplate.organizationId) {
                    // Fetch organization data
                    const result = await getOrganization(selectedTemplate.organizationId)

                    if (result && result.organization) {
                        setTemplateOrganization(result.organization)
                        setTemplateApp(null)
                    } else {
                        logger.error('TemplateSelector: Failed to load template organization data:', {
                            hasResult: !!result,
                            resultKeys: result ? Object.keys(result) : null
                        })
                        dispatch(showPrompt({
                            title: 'Preview Error',
                            message: 'Failed to load template preview data.',
                            type: PromptType.Confirm,
                            cancelMessage: null
                        }))
                    }
                } else {
                    logger.warn('TemplateSelector: Template has no appId or organizationId:', {
                        templateId: selectedTemplate.id,
                        templateName: selectedTemplate.name,
                        templateKeys: Object.keys(selectedTemplate)
                    })
                    dispatch(showPrompt({
                        title: 'Preview Error',
                        message: 'This template is missing ownership information and cannot be previewed.',
                        type: PromptType.Confirm,
                        cancelMessage: null
                    }))
                }
            } catch (error) {
                logger.error('TemplateSelector: Failed to fetch template data:', error, {
                    templateId: selectedTemplate.id,
                    templateName: selectedTemplate.name,
                    errorMessage: error.message,
                    errorStack: error.stack
                })
                dispatch(showPrompt({
                    title: 'Preview Error',
                    message: 'An error occurred while loading the template preview.',
                    type: PromptType.Confirm,
                    cancelMessage: null
                }))
            } finally {
                setLoadingPreview(false)
            }
        }

        fetchTemplateData()
    }, [selectedTemplate])

    async function fetchTemplates() {
        setLoadingTemplates(true)
        try {
            const result = await dispatch(fetchTemplatePages({
                organizationId: organization?.id
            }))

            if (result.payload && result.payload.pages) {
                setTemplatePages(result.payload.pages)
                logger.info('Loaded template pages:', result.payload.pages.length)

                // Auto-expand first tag group
                if (result.payload.pages.length > 0) {
                    const firstPage = result.payload.pages[0]
                    if (firstPage.tags && firstPage.tags.length > 0) {
                        const firstTag = firstPage.tags.split(',')[0].trim()
                        if (firstTag) {
                            setExpandedTags(new Set([firstTag]))
                        }
                    }
                }
            }
        } catch (error) {
            logger.error('Failed to fetch template pages:', error)
            dispatch(showPrompt({
                title: 'Error',
                message: 'Failed to load template pages. Please try again.',
                type: PromptType.Confirm,
                cancelMessage: null
            }))
        } finally {
            setLoadingTemplates(false)
        }
    }

    function handleSelectTemplate(template) {
        logger.info('Selected template:', template.name, 'appId:', template.appId, 'organizationId:', template.organizationId)
        setSelectedTemplate(template)
    }

    function toggleTagExpansion(tag: string) {
        const newExpanded = new Set(expandedTags)
        if (newExpanded.has(tag)) {
            newExpanded.delete(tag)
        } else {
            newExpanded.add(tag)
        }
        setExpandedTags(newExpanded)
    }

    async function handleCloneFromTemplate(templatePage) {
        if (!templatePage) return

        setGeneratingPage(true)

        try {
            let result
            const proms = []

            // Show pending dialog immediately
            proms.push(dispatch(showPrompt({
                title: 'Creating Page',
                message: 'Please wait while we create your new page from the template...',
                successMessage: 'Page created successfully! You will now be taken to your new page.',
                type: PromptType.Pending,
            })))

            // Determine where to clone based on current context
            if (currentProject) {
                // Clone to current app/project
                logger.info('Cloning template to current project:', currentProject.meta?.name || currentProject.name)
                proms.push(dispatch(cloneTemplateToContext({
                    pageId: templatePage.id,
                    targetContext: { type: 'app', id: currentProject.meta?.id || currentProject.id },
                    usePlaceholders
                })))
            } else if (organization) {
                // Clone to current organization
                logger.info('Cloning template to current organization:', organization.name)
                proms.push(dispatch(cloneTemplateToContext({
                    pageId: templatePage.id,
                    targetContext: { type: 'organization', id: organization.id },
                    usePlaceholders
                })))
            } else {
                throw new Error('No valid clone target found')
            }

            const results = await Promise.all(proms)
            result = results[results.length - 1] // The last result is the cloning result

            if (onNavigate) {
                onNavigate(result.payload?.page, {
                    targetApp: currentProject,
                    targetOrganization: organization,
                    reload: false
                })
            } else {
                // Check if current URL includes organization link
                const currentUrl = window.location.pathname
                const includeOrgInUrl = organization && currentUrl.includes(`/${organization.link}/`)

                let finalUrl = '/'
                if (organization && !standaloneOrg && currentUrl.includes(`/${organization.link}/`)) {
                    finalUrl += `${organization.link}/`
                }
                if(currentProject && !standaloneOrg && !standaloneApp && currentProject.meta?.link) {
                    finalUrl += `${currentProject.meta.link}/`
                }
                if (result.payload?.page) {
                    finalUrl += result.payload.page.link
                }

                dispatch(navigate(finalUrl))
            }

            // Close the template selector immediately
            onClose()
        } catch (error) {
            logger.error('Failed to clone template:', error)
            dispatch(showPrompt({
                title: 'Error',
                message: 'Failed to create page from template. Please try again.',
                type: PromptType.Confirm,
                cancelMessage: null,
                confirmMessage: 'OK'
            }))
        } finally {
            setGeneratingPage(false)
        }
    }

    function getTemplateSelector() {
        if (loadingTemplates) {
            return (
                <div className="template-selector">
                    <div className="content-editor-topbar">
                        <IconButton noBg icon="fas fa-arrow-left" onClick={onClose} />
                        <div className="content-editor-title">
                            <h4>Select Template</h4>
                        </div>
                    </div>
                    <div className="template-selector-loading" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        padding: '40px',
                        color: 'var(--primary-dark)'
                    }}>
                        <Spinner />
                        <p style={{ marginTop: '20px', color: 'var(--primary-dark)' }}>
                            Loading templates...
                        </p>
                    </div>
                </div>
            )
        }

        const availableTemplates = templatePages.filter(p => p.template) || []

        if (availableTemplates.length === 0) {
            return (
                <div className="template-selector">
                    <div className="content-editor-topbar">
                        <IconButton noBg icon="fas fa-arrow-left" onClick={onClose} />
                        <div className="content-editor-title">
                            <h4>Select Template</h4>
                        </div>
                    </div>
                    <div className="template-selector-empty">
                        <Icon icon="fas fa-inbox" />
                        <h4>No Templates Available</h4>
                        <p>There are no template pages available to clone.</p>
                    </div>
                </div>
            )
        }

        const tagGroups: Map<string, typeof availableTemplates> = new Map()
        const untaggedTemplates = []

        availableTemplates.forEach(template => {
            if (template.tags && template.tags.length > 0) {
                const tags = template.tags.split(',').map(t => t.trim()).filter(t => t.length > 0)
                if (tags.length === 0) {
                    untaggedTemplates.push(template)
                } else {
                    tags.forEach(tag => {
                        if (!tagGroups.has(tag)) {
                            tagGroups.set(tag, [])
                        }
                        tagGroups.get(tag).push(template)
                    })
                }
            } else {
                untaggedTemplates.push(template)
            }
        })

        const sortedTags = Array.from(tagGroups.keys()).sort((a, b) => a.localeCompare(b))

        return (
            <div className="template-selector scrollable">
                <div className="content-editor-topbar">
                    <IconButton
                        noBg
                        icon="fas fa-arrow-left"
                        onClick={onClose}
                        title="Close template selector"
                    />
                    <div className="content-editor-title">
                        <h4>Select Template</h4>
                    </div>
                </div>

                <div className="template-groups">
                    {sortedTags.map(tag => {
                        const templates = tagGroups.get(tag)
                        const isExpanded = expandedTags.has(tag)

                        return (
                            <div key={tag} className="template-tag-group">
                                <div
                                    className="template-tag-header animate__animated animate__fadeIn animate__fastest"
                                    onClick={() => toggleTagExpansion(tag)}
                                >
                                    <Icon noBg icon={isExpanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'} />
                                    <h5>{tag.toReadable()}</h5>
                                    <span className="template-count">({templates.length})</span>
                                </div>
                                {isExpanded && (
                                    <div className="template-tag-content">
                                        {templates.map(template => (
                                            <TemplateTile
                                                key={template.id}
                                                template={template}
                                                config={config}
                                                isSelected={selectedTemplate?.id === template.id}
                                                onSelect={handleSelectTemplate}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}

                    {untaggedTemplates.length > 0 && (
                        <div className="template-tag-group">
                            <div
                                className="template-tag-header animate__animated animate__fadeIn animate__fastest"
                                onClick={() => toggleTagExpansion('_untagged')}
                            >
                                <Icon noBg icon={expandedTags.has('_untagged') ? 'fas fa-chevron-down' : 'fas fa-chevron-right'} />
                                <h5>Other Templates</h5>
                                <span className="template-count">({untaggedTemplates.length})</span>
                            </div>
                            {expandedTags.has('_untagged') && (
                                <div className="template-tag-content">
                                    {untaggedTemplates.map(template => (
                                        <TemplateTile
                                            key={template.id}
                                            template={template}
                                            config={config}
                                            isSelected={selectedTemplate?.id === template.id}
                                            onSelect={handleSelectTemplate}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {selectedTemplate && (
                    <div className="template-selector-actions">
                        <div className="template-options" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <TooltipLabel
                                htmlFor="use-placeholders"
                                tooltip="Use placeholder images instead of copying all template media. This creates a single placeholder image entry and uses it for all media in the template."
                                style={{ margin: 0 }}
                            >
                                Use Placeholder Images
                            </TooltipLabel>
                            <Checkbox
                                id="use-placeholders"
                                value={usePlaceholders}
                                onChange={setUsePlaceholders}
                                defaultChecked={true}
                            />
                        </div>
                        <Button
                            onClick={() => handleCloneFromTemplate(selectedTemplate)}
                            disabled={generatingPage}
                            icon="fas fa-hand-pointer"
                            secondary
                        >
                            {generatingPage ? 'Creating...' : 'Pick Template'}
                        </Button>
                    </div>
                )}
            </div>
        )
    }

    function getTemplatePreview() {
        if (!selectedTemplate) {
            return (
                <div className="template-preview-empty" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    padding: '60px 40px',
                    textAlign: 'center',
                    backgroundColor: '#f8f9fa'
                }}>
                    <Icon
                        icon="fas fa-eye"
                        style={{
                            fontSize: '64px',
                            color: '#dee2e6',
                            marginBottom: '24px'
                        }}
                    />
                    <h4 style={{
                        margin: '0 0 12px 0',
                        color: '#495057',
                        fontSize: '18px',
                        fontWeight: '600'
                    }}>
                        Select a Template
                    </h4>
                    <p style={{
                        margin: '0',
                        color: '#6c757d',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        maxWidth: '300px'
                    }}>
                        Choose a template from the left to preview it here.
                    </p>
                </div>
            )
        }

        if (loadingPreview) {
            return (
                <div className="template-preview-loading" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    padding: '40px'
                }}>
                    <Spinner />
                    <p style={{ marginTop: '20px', color: 'var(--primary-dark)' }}>
                        Loading preview...
                    </p>
                </div>
            )
        }

        // Check if we have the required data
        if (!templateOrganization && !templateApp) {
            return (
                <div className="template-preview-empty">
                    <Icon icon="fas fa-exclamation-triangle" />
                    <h4>Preview Unavailable</h4>
                    <p>Unable to load preview data for this template.</p>
                </div>
            )
        }

        return (
            <div className="template-preview" ref={previewContainerRef}>
                <PreviewHeader title={`Preview: ${selectedTemplate.name}`} />

                <div className="template-preview-container" style={{
                    height: 'calc(100% - 80px)',
                    overflow: 'hidden',
                    position: 'relative'
                }}>
                    <div className="template-preview-scale-wrapper" style={{
                        transform: 'scale(0.8)',
                        transformOrigin: 'top left',
                        width: '125%',
                        height: '125%'
                    }}>
                        <MemoryRouter>
                            <DynamicPage
                                app={templateApp}
                                organization={templateOrganization}
                                pageLink={selectedTemplate.link}
                                splitIndex={0}
                                breadCrumbs={false}
                                previewMode={true}
                            />
                        </MemoryRouter>
                    </div>
>
                </div>
            </div>
        )
    }

    return (
        <div className="inline-content-editor template-mode fullscreen">
            {generatingPage && <Spinner invert showAfter={0} />}
            <div className="template-mode-container">
                <div className="template-mode-left content-editor">
                    {getTemplateSelector()}
                </div>
                <div className="template-mode-right">
                    {getTemplatePreview()}
                </div>
            </div>
        </div>
    )
}
